import {
  MultiCheckDropdownMenu,
  SelectOptionWithAvatar,
  DepartmentMultiValue,
  SelectParentChildRootOption,
} from '@/components/formBuilder/SelectComponents';

// Common options that are used across multiple filter configurations
export const COMMON_OPTIONS = {
  // Boolean options
  yesNoOptions: [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ],

  // Rejection type options
  rejectionTypeOptions: [
    { value: 'User', label: 'Manual' },
    { value: 'Auto', label: 'AI' },
  ],

  // AI review status options
  aiReviewStatusOptions: [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ],

  // AI review score options (1-100)
  aiReviewScoreOptions: Array.from({ length: 100 }, (_, i = 1) => ({
    value: i + 1,
    label: (i + 1).toString(),
  })),

  // Flow status options
  flowStatusOptions: [
    { value: 'active', label: 'Active' },
    { value: 'draft', label: 'Draft' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'archived', label: 'Archived' },
  ],

  // Application status options
  applicationStatusOptions: [
    { label: 'Needs scheduling', value: 'needs_scheduling' },
    { label: 'Needs action', value: 'needs_action' },
    { label: 'Meeting scheduled', value: 'meeting_scheduled' },
    { label: 'Email scheduled', value: 'email_scheduled' },
    { label: 'Assessment sent', value: 'assessment_sent' },
    { label: 'Awaiting feedback', value: 'awaiting_feedback' },
    { label: 'Needs offer', value: 'needs_offer' },
    { label: 'Needs offer approval', value: 'needs_offer_approval' },
    {
      label: 'Waiting on offer acceptance',
      value: 'waiting_on_offer_acceptance',
    },
    { label: 'Offer accepted', value: 'offer_accepted' },
    { label: 'Needs decision', value: 'needs_decision' },
  ],

  applicationStatusOptionsMap: {
    needs_scheduling: 'Needs scheduling',
    needs_action: 'Needs action',
    meeting_scheduled: 'Meeting scheduled',
    email_scheduled: 'Email scheduled',
    assessment_sent: 'Assessment sent',
    awaiting_feedback: 'Awaiting feedback',
    needs_offer: 'Needs offer',
    needs_offer_approval: 'Needs offer approval',
    waiting_on_offer_acceptance: 'Waiting on offer acceptance',
    offer_accepted: 'Offer accepted',
    needs_decision: 'Needs decision',
  },

  // Offer status options
  offerStatusOptions: [
    {
      label: 'Draft',
      value:
        'draft, pending_document_generation, document_generation_error, document_generated',
    },
    { label: 'Pending approval', value: 'pending_approval' },
    { label: 'Offer approved', value: 'approved_internal' },
    { label: 'Offer rejected', value: 'rejected_internal' },
    { label: 'Offer sent', value: 'awaiting_on_candidate' },
    { label: 'Accepted by candidate', value: 'accepted_by_candidate' },
    { label: 'Rejected by candidate', value: 'rejected_by_candidate' },
    { label: 'sending offer', value: 'candidate_email_in_progress' },
    {
      label: 'Sending offer with Docusign',
      value: 'docusign_send_in_progress',
    },
    {
      label: 'Awaiting company signer',
      value: 'awaiting_on_company_signer',
    },
    {
      label: 'Offer delivery failed',
      value:
        'docusign_send_failed, candidate_email_failed, pandadocs_send_failed',
    },
    {
      label: 'Rejected by company signer',
      value: 'rejected_by_company_signer',
    },
    {
      label: 'Pandadoc send in progress',
      value: 'pandadocs_send_in_progress',
    },
    {
      label: 'Pandadoc send failed',
      value: 'pandadocs_send_failed',
    },
    {
      label: 'Cancelled',
      value: 'cancelled',
    },
  ],
};

// Common fetch configurations
export const COMMON_FETCH_CONFIGS = {
  company: {
    method: 'get',
    endPoint: `/search/circles/autocomplete?filter_name=company&filter_value=`,
    httpType: 'v1',
    processResponse: (res) =>
      res?.data?.autocomplete?.map((item) => ({
        label: item.name,
        value: {
          id: item?.id,
          value: item?.name,
        },
      })),
  },
  institution: {
    method: 'get',
    endPoint: `academics/institutions?q=`,
    processResponse: (res) =>
      res?.data?.map((item) => ({
        label: item?.name,
        value: item,
        ...item,
      })),
  },
};

// Common filter configurations for shared sections only
export const COMMON_FILTER_CONFIGS = {
  // Experience section
  position: {
    section: 'Experience',
    filterLabel: 'Title',
    name: 'position',
    label: '',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    isRequired: false,
    type: 'plainInput',
    styles: { height: '38px' },
    isNewFilter: true,
    isAppendToBody: true,
  },

  current_company_ids: {
    section: 'Experience',
    filterLabel: 'Current company',
    name: 'current_company_ids',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: false,
    isBoolean: false,
    label: '',
    isRequired: false,
    useInput: true,
    updateEndpointOnChange: true,
    fetchConfig: COMMON_FETCH_CONFIGS.company,
    validationType: 'array',
    type: 'asyncSelect',
    isNewFilter: true,
    isAppendToBody: true,
  },

  past_company_id: {
    section: 'Experience',
    filterLabel: 'Past company',
    name: 'past_company_id',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: false,
    isBoolean: false,
    label: '',
    isRequired: false,
    useInput: true,
    updateEndpointOnChange: true,
    fetchConfig: COMMON_FETCH_CONFIGS.company,
    validationType: 'array',
    type: 'asyncSelect',
    isNewFilter: true,
    isAppendToBody: true,
  },

  past_company_ids: {
    section: 'Experience',
    filterLabel: 'Past company',
    name: 'past_company_ids',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: false,
    isBoolean: false,
    label: '',
    isRequired: false,
    useInput: true,
    updateEndpointOnChange: true,
    fetchConfig: COMMON_FETCH_CONFIGS.company,
    validationType: 'array',
    type: 'asyncSelect',
    isNewFilter: true,
    isAppendToBody: true,
  },

  company_industry: (industryOptions) => ({
    section: 'Experience',
    filterLabel: 'Industry',
    name: 'company_industry',
    labelField: 'value',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: industryOptions.map((item) => ({
      value: item?.key,
      label: item?.value,
    })),
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  company_employee_range: (employeeRangeOptions) => ({
    section: 'Experience',
    filterLabel: 'Employee size',
    name: 'company_employee_range',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: employeeRangeOptions.map((item) => ({
      value: item?.key,
      label: item?.value,
    })),
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  years_of_experience: {
    section: 'Experience',
    filterLabel: 'Years of experience',
    name: 'years_of_experience',
    label: '',
    isMulti: false,
    isCompare: true,
    isBoolean: false,
    isRequired: false,
    type: 'kInput',
    inputType: 'number',
    styles: { height: '38px' },
    isNewFilter: true,
    isAppendToBody: true,
  },

  // Education section
  degree_ids: (getDegreeOptions) => ({
    section: 'Education',
    filterLabel: 'Degree',
    name: 'degree_ids',
    isMulti: true,
    isCompare: false,
    isSearchable: false,
    isBoolean: false,
    label: '',
    isRequired: false,
    useInput: true,
    updateEndpointOnChange: true,
    options: getDegreeOptions?.map((item) => ({
      value: item?.id,
      label: item?.name,
    })),
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  institute_ids: {
    section: 'Education',
    filterLabel: 'Institution',
    name: 'institute_ids',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: false,
    isBoolean: false,
    label: '',
    isRequired: false,
    useInput: true,
    updateEndpointOnChange: true,
    fetchConfig: COMMON_FETCH_CONFIGS.institution,
    validationType: 'array',
    type: 'asyncSelect',
    isNewFilter: true,
    isAppendToBody: true,
  },

  graduation_year: {
    section: 'Education',
    filterLabel: 'Graduation year',
    name: 'graduation_year',
    isCompare: false,
    isMulti: false,
    isBoolean: true,
    label: '',
    isRequired: false,
    validationType: 'array',
    type: 'yearRangePicker',
    isCustomize: false,
    isNewFilter: true,
    fieldKey: 'date',
    isAppendToBody: true,
  },

  // Job section
  activity_status: {
    section: 'Job',
    filterLabel: 'Application status',
    name: 'activity_status',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: COMMON_OPTIONS.applicationStatusOptions,
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  },

  application_status: {
    section: 'Job',
    filterLabel: 'Application status',
    name: 'application_status',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: COMMON_OPTIONS.applicationStatusOptions,
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  },

  rejection_reason_ids: (
    rejectionReasons,
    generateGroupOptions,
    rejectedFilter
  ) => ({
    section: 'Job',
    filterLabel: 'Rejection reasons',
    name: 'rejection_reason_ids',
    label: '',
    isMulti: true,
    isDisabled: !rejectedFilter,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    isRequired: false,
    disabledFilterInfoMessage: `Select 'Rejected' to use this filter`,
    inactiveRowTooltip: `This filter applies to rejected candidates only`,
    options: generateGroupOptions(rejectionReasons || []),
    includeParentInValue: false,
    components: {
      Menu: MultiCheckDropdownMenu,
    },
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  interviewers: (usersOptions) => ({
    section: 'Job',
    filterLabel: 'Interviewers',
    name: 'interviewers',
    labelField: 'name',
    label: '',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    isRequired: false,
    customFormatLabel: SelectOptionWithAvatar,
    options: usersOptions,
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  hire_date: {
    section: 'Job',
    filterLabel: 'Hire date',
    name: 'hire_date',
    isCompare: false,
    isMulti: false,
    isBoolean: true,
    label: '',
    isRequired: false,
    validationType: 'array',
    type: 'filterDateRangePicker',
    isCustomize: false,
    isNewFilter: true,
    fieldKey: 'date',
    isAppendToBody: true,
  },

  rejection_type: {
    section: 'Job',
    filterLabel: 'Rejected by type',
    name: 'rejection_type',
    labelField: 'value',
    isMulti: false,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: COMMON_OPTIONS.rejectionTypeOptions,
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  },

  latest_application_date: {
    section: 'Job',
    filterLabel: 'Last applied date',
    name: 'latest_application_date',
    isCompare: false,
    isMulti: false,
    isBoolean: true,
    label: '',
    isRequired: false,
    validationType: 'array',
    type: 'filterDateRangePicker',
    isCustomize: false,
    isNewFilter: true,
    fieldKey: 'date',
    isAppendToBody: true,
  },

  ai_review_score_value: {
    section: 'Job',
    filterLabel: 'AI screening score',
    name: 'ai_review_score_value',
    labelField: 'value',
    isMulti: false,
    isCompare: true,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: COMMON_OPTIONS.aiReviewScoreOptions,
    validationType: 'object',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  },

  ai_review_status: {
    section: 'Job',
    filterLabel: 'AI screening score status',
    name: 'ai_review_status',
    labelField: 'label',
    isMulti: false,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: COMMON_OPTIONS.aiReviewStatusOptions,
    validationType: 'object',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  },

  last_stage_time: {
    section: 'Job',
    filterLabel: 'Last Stage Change',
    name: 'last_stage_time',
    isCompare: true,
    isMulti: false,
    isBoolean: false,
    label: '',
    isRequired: false,
    validationType: 'array',
    type: 'filterDatePicker',
    isCustomize: false,
    isNewFilter: true,
    fieldKey: 'date',
    isAppendToBody: true,
  },

  // Offer section
  offer_status: {
    section: 'Offer',
    filterLabel: 'Offer status',
    name: 'offer_status',
    labelField: 'label',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    label: '',
    isRequired: false,
    options: COMMON_OPTIONS.offerStatusOptions,
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  },

  offer_sent_date: {
    section: 'Offer',
    filterLabel: 'Latest offer sent date',
    name: 'offer_sent_date',
    isCompare: false,
    isMulti: false,
    isBoolean: true,
    label: '',
    isRequired: false,
    validationType: 'array',
    type: 'filterDateRangePicker',
    isCustomize: false,
    isNewFilter: true,
    fieldKey: 'date',
    isAppendToBody: true,
  },

  offer_department: (departments) => ({
    section: 'Offer',
    filterLabel: 'Offer department',
    name: 'offer_department',
    label: '',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    isRequired: false,
    options: departments,
    valueKey: 'id',
    labelKey: 'name',
    validationType: 'array',
    components: {
      Menu: SelectParentChildRootOption,
      MultiValue: DepartmentMultiValue,
    },
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  offer_office: (offices) => ({
    section: 'Offer',
    filterLabel: 'Offer office',
    name: 'offer_office',
    label: '',
    isMulti: true,
    isCompare: false,
    isSearchable: true,
    isBoolean: false,
    isRequired: false,
    options: offices?.map((item) => ({
      value: item?.id,
      label: item?.name,
    })),
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),

  offer_start_date: {
    section: 'Offer',
    filterLabel: 'Offer start date',
    name: 'offer_start_date',
    isCompare: false,
    isMulti: false,
    isBoolean: true,
    label: '',
    isRequired: false,
    validationType: 'array',
    type: 'filterDateRangePicker',
    isCustomize: false,
    isNewFilter: true,
    fieldKey: 'date',
    isAppendToBody: true,
  },

  // Flows section
  outreach_sourced_by: (usersOptions) => ({
    section: 'Flows',
    filterLabel: 'Sourced by',
    name: 'outreach_sourced_by',
    label: '',
    isMulti: true,
    isCompare: false,
    labelField: 'name',
    isSearchable: true,
    isBoolean: false,
    isRequired: false,
    customFormatLabel: SelectOptionWithAvatar,
    options: usersOptions,
    validationType: 'array',
    type: 'select',
    isNewFilter: true,
    isAppendToBody: true,
  }),
};

// Helper function to generate group options for rejection reasons
export const generateGroupOptions = (options) => {
  const uniqueGroups = options.reduce((groups, item) => {
    if (!groups.includes(item.group)) {
      groups.push(item.group);
    }
    return groups;
  }, []);

  // Map existing items and add new options dynamically
  return [
    ...options.map((item) => ({
      parent_id: item.group,
      value: item.id,
      label: item.reason,
    })),
    ...uniqueGroups.map((group) => ({
      value: group,
      label: group
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    })),
  ];
};
